import { useState } from 'react'
import type * as React from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

// The signed-in identity control: a brand-coloured initial that opens who you
// are and how to leave. Extracted because it appears on surfaces that place it
// differently — the chat header's left slot, the booking view's header right —
// and three hand-rolled copies of the same dropdown is how they drift.
//
// The avatar is the same size and shape wherever it lands, so the header keeps
// its 56px rhythm on every client screen.

export interface AccountMenuProps {
  /** Primary line in the panel — the name or email of the signed-in user. */
  label: string
  /** Secondary line: the role, or nothing when there is nothing worth saying. */
  sublabel?: string
  color: string
  onSignOut: () => void
  /**
   * Which edge the panel hangs from. The chat header puts the avatar on the
   * LEFT (headerLeft) and the booking header on the right, so the panel has to
   * follow it or it opens off the side of the screen.
   */
  align?: 'start' | 'end'
  /**
   * Items rendered above Sign out, each already a DropdownMenuItem. Signing out
   * stays last and is always present: a menu whose only job is identity must
   * still offer the way out.
   */
  children?: React.ReactNode
}

export function AccountMenu({
  label,
  sublabel,
  color,
  onSignOut,
  align = 'end',
  children,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const initial = label.charAt(0).toUpperCase() || '?'

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Account menu" className="shrink-0 p-0.5">
          <div
            className={cn(
              'flex size-8 items-center justify-center rounded-full',
              open && 'opacity-80',
            )}
            style={{ backgroundColor: color }}
          >
            <span className="text-[13px] font-bold text-white">{initial}</span>
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align={align} className="w-[220px] overflow-hidden p-0">
        <div className="flex flex-col gap-0.5 px-3.5 py-3">
          <p className="truncate text-xs font-medium text-foreground">{label}</p>
          {sublabel && (
            <p className="text-[11px] capitalize text-muted-foreground/70">{sublabel}</p>
          )}
        </div>
        <DropdownMenuSeparator className="mx-0 my-0 bg-muted" />
        {children}
        <DropdownMenuItem className="rounded-none px-3.5 py-3" onSelect={onSignOut}>
          <span className="text-sm font-medium text-foreground">Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
